import { DataManager } from '@syncfusion/ej2-data';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { api } from '../shared/communication-objects/api-path';
import { AppDialogService } from '@services/app-dialog.service';
import { LogService } from '@services/log.service';
import { TankService } from '@services/tank.service';
import { CacheBuster } from 'ts-cacheable';
import { TankSwapFormDialogWrapperComponent } from '../page/tank/components/tank-setup/widgets/tank-swap-form-dialog-wrapper/tank-swap-form-dialog-wrapper.component';
import {
  Observable,
  take,
  tap,
  switchMap,
  timeout,
  catchError,
  finalize,
  timer,
  throwError,
  filter,
  Subject,
} from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { DropDown } from '@interfaces/drop-down.interface';
import { _ITankSetupFormBase } from './tank-setup.service';
import { TokenService } from '@services/token.service';
import { AuthUrlAdaptor } from '../shared/communication-objects/auth-url.adaptor';

@Injectable({
  providedIn: 'root',
})
export class TankRtuDeviceService {
  private readonly apiURL: string = 'tank-rtu-device';
  private readonly SWAP_CHECK_TIMEOUT = 10 * 1000; // 10 seconds total (5 attempts * 2 seconds)
  private readonly SWAP_CHECK_INTERVAL_MS = 1000; // check every second.

  constructor(
    private httpClient: DSRESTClient,
    private logger: LogService,
    private appDialogService: AppDialogService,
    private tankService: TankService,
    private translate: TranslateService,
    private tokenService: TokenService,
  ) {}

  assignHostDevice(
    uniqueId: number,
    request: ISwapRTURequest,
  ): Observable<ISwapRTUResponse> {
    return this.httpClient.postForResponse<ISwapRTUResponse>(
      api`tank-rtu-device/${uniqueId}/assign-client-to-host`,
      request,
    );
  }

  getTankSetupRtuDeviceFormVM(
    uniqueId: number,
  ): Observable<IRtuDeviceSetupFormResponse> {
    return this.httpClient.getDirect<IRtuDeviceSetupFormResponse>(
      api`tank-rtu-device/${uniqueId}/get-rtu-device-setup-form`,
    );
  }

  showSwapFormDialog(
    uniqueId: number,
  ): MatDialogRef<TankSwapFormDialogWrapperComponent> {
    return this.appDialogService.showDialogReturnRef(
      TankSwapFormDialogWrapperComponent,
      { uniqueId, isAssign: false },
    );
  }

  showAssignFormDialog(
    uniqueId: number,
    deviceId?: number | null | undefined,
    contextDeviceId?: number | null | undefined,
  ): MatDialogRef<TankSwapFormDialogWrapperComponent> {
    return this.appDialogService.showDialogReturnRef(
      TankSwapFormDialogWrapperComponent,
      { uniqueId, isAssign: true, deviceId: deviceId, contextDeviceId: contextDeviceId },
    );
  }

  showAssignHostFormDialog(    
    uniqueId: number,
    deviceId?: number | null | undefined,
    contextDeviceId?: number | null | undefined,
  ): MatDialogRef<TankSwapFormDialogWrapperComponent> {
    return this.appDialogService.showDialogReturnRef(
      TankSwapFormDialogWrapperComponent,
      { uniqueId, isAssign: true, deviceId: deviceId, contextDeviceId: contextDeviceId, isHostAssignment: true },
    );
  }

  getSwapRTUFormVM(uniqueId: number): Observable<any> {
    return this.httpClient.getDirect<any>(
      api`tank-rtu-device/${uniqueId}/swap-rtu`,
    );
  }

  getMinRtuLookupDM(
    includeAllOrgs: boolean = true,
    uniqueId: number,
    isGateway: boolean
  ): DataManager {
    return new DataManager({
      url: `api/v1/${this.apiURL}/${uniqueId}/min-rtu-lookup?includeAllOrgs=${includeAllOrgs}&isGateway=${isGateway}`,
      // AuthUrlAdaptor, not UrlAdaptor - a DataManager fetches outside HttpClient, so nothing else
      // attaches the bearer token this [Authorize] endpoint needs.
      adaptor: new AuthUrlAdaptor(this.tokenService),
    });
  }

  getSwapRTUDevice(deviceId: number): Observable<ISwapRTUDevice> {
    return this.httpClient.getDirect<ISwapRTUDevice>(
      api`tank-rtu-device/get-swap-rtu-device/${deviceId}`,
    );
  }

  swapComplete: Subject<ISwapRTUCompleteResponse>;
  swapComplete$: Observable<ISwapRTUCompleteResponse>;

  checkSwapComplete(
    uniqueId: number,
    currentDeviceId: number,
    swapDeviceId: number,
  ): Observable<ISwapRTUCompleteResponse> {

    this.swapComplete = new Subject<ISwapRTUCompleteResponse>();
    this.swapComplete$ = this.swapComplete.asObservable();

    this.httpClient.getDirect<ISwapRTUCompleteResponse>(
      api`tank-rtu-device/${uniqueId}/swap-complete?currentDeviceId=${currentDeviceId}&swapDeviceId=${swapDeviceId}`,
    ).subscribe({
      next: (response) => {
        this.swapComplete.next(response);
      },
      error: (error) => {
        this.logger.error('Error checking swap complete', error);
      },
      complete: () => {
        this.swapComplete.complete();
      },
    }
    );
    return this.swapComplete$;
  }

  checkAssignComplete(
    uniqueId: number,
    deviceId: number,
  ): Observable<ISwapRTUCompleteResponse> {
    return this.httpClient.getDirect<ISwapRTUCompleteResponse>(
      api`tank-rtu-device/${uniqueId}/assign-complete?deviceId=${deviceId}`,
    );
  }

  @CacheBuster({
    isInstant: true,
  })
  swapRTUDevice(
    uniqueId: number,
    currentDevice: ISwapRTUDevice,
    swapRTUDevice: ISwapRTUDevice,
    isAlternate: boolean = false,
    alternateLocationId: number = 0,
    isAssign: boolean = false,
  ): Observable<ISwapRTUCompleteResponse> {
    const progressRef = this.appDialogService.showProgressDialog(
      this.translate.instant(
        isAssign
          ? 'TankSwap.Progress.Title.Assign'
          : 'TankSwap.Progress.Title.Swap',
      ),
      this.translate.instant(
        isAssign
          ? 'TankSwap.Progress.Message.Assign'
          : 'TankSwap.Progress.Message.Swap',
      ),
    );

    const request: ISwapRTURequest = isAssign
      ? {
          swapRTUDeviceId: swapRTUDevice.deviceId,
          isAlternate: isAlternate ?? false,
          alternateLocationId: alternateLocationId ?? 0,
        }
      : {
          currentDeviceId: currentDevice.deviceId,
          swapRTUDeviceId: swapRTUDevice.deviceId,
          isAlternate: isAlternate ?? false,
          alternateLocationId: alternateLocationId ?? 0,
        };

    return this.initiateSwap(uniqueId, request, isAssign).pipe(
      switchMap((swapResponse: ISwapRTUResponse) => {
        if(!swapResponse.success) {
          const errorMessage = this.translate.instant('TankSwap.Error' + swapResponse.errors[0].stringResourceKey);
          this.showErrorMessage(errorMessage, swapResponse);
          return throwError(() => new Error(errorMessage));
        }
        return this.pollSwapAssignStatus(uniqueId, request, isAssign)
      }
        ),
      timeout(this.SWAP_CHECK_TIMEOUT),
      // handle success case
      tap({
        next: (swapCompleteResponse) => {
          if (swapCompleteResponse.swapComplete) {
            this.showSuccessMessage(
              this.translate.instant(
                isAssign
                  ? 'TankSwap.Snackbar.Assigned'
                  : 'TankSwap.Snackbar.Swapped',
              ),
            );
            this.tankService.reloadKeyTankInfo();
          }
        },
      }),
      // handle timeout error and other errors
      catchError((error) => {
        let errorMessage = error.message ?? null;
        if (error.name === 'TimeoutError') {
          errorMessage = this.translate.instant(
            isAssign
              ? 'TankSwap.Error.AssignTimedOut'
              : 'TankSwap.Error.SwapTimedOut',
          );
        }
        this.showErrorMessage(errorMessage, error);
        throw throwError(() => new Error(errorMessage));
      }),
      // Always close the progress dialog
      finalize(() => {
        progressRef.close();
      }),
    );
  }

  private initiateSwap(
    uniqueId: number,
    request: ISwapRTURequest,
    isAssign: boolean,
  ): Observable<ISwapRTUResponse> {
    return this.httpClient.postForResponse<ISwapRTUResponse>(
      api`tank-rtu-device/${uniqueId}/swap-rtu-device`,
      request,
    );
  }

  private pollSwapAssignStatus(
    uniqueId: number,
    request: ISwapRTURequest,
    isAssign: boolean,
  ): Observable<any> {
    return timer(0, this.SWAP_CHECK_INTERVAL_MS).pipe(
      switchMap(() =>
        isAssign
          ? this.checkAssignComplete(uniqueId, request.swapRTUDeviceId)
          : this.checkSwapComplete(
              uniqueId,
              request.currentDeviceId,
              request.swapRTUDeviceId,
            ),
      ),
      tap((response) => {
        console.log('polling swap response', response);
      }),
      filter((response) => {
        console.log('filterering poll response', response);
        return response.swapComplete === true;
      }),
      take(1), // always take the first response where swapComplete is true
    );
  }

  private showSuccessMessage(message: string): void {
    this.appDialogService.showGeneralMessage(message);
  }

  private showErrorMessage(message: string | null, response: any): void {
    if (response?.error?.errors) {
      response.error.errors.forEach((error: any) => {
        this.appDialogService.showErrorMessage(
          this.translate.instant(error.stringResourceKey),
        );
      });
    } else {
      this.appDialogService.showErrorMessage(
        message ?? this.translate.instant('TankSwap.Error.SwapFailed'),
      );
    }
  }

  unassignTank(tankId: number): Observable<any> {
    const apiResponse = this.httpClient.post(
      api`tank-rtu-device/unassignTank`, tankId
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('TankService/UnassignTank Response', response),
      ),
    );
  }
}

export interface ISwapRTUDevice {
  deviceId: number;
  deviceName: string;
  tankName: string;
  tankId: number;
  tankNumber: string;
  location: string;
  address: string;
  minNumber: number;
  rtuNumber: number;
  sensorType: string;
  sensorLength: string;
  sensorRtuModelType: string;
  organizationName: string;
  isHostDevice: boolean;
  hasV2Protocol: boolean;
  isClientDevice: boolean;
}

export interface ISwapRTURequest {
  currentDeviceId?: number;
  swapRTUDeviceId: number;
  isAlternate: boolean;
  alternateLocationId: number;
}

export interface ISwapRTUResponse {
  success: boolean;
  errors: ISaveOrUpdateError[];
}

export interface ISwapRTUCompleteResponse {
  swapComplete: boolean;
  oldTankDevice?: ISwapRTUDevice;
  newTankDevice?: ISwapRTUDevice;
}

export interface ISaveOrUpdateError {
  errorNo: number;
  stringResourceKey: string;
}

export interface IRtuDeviceSetupFormResponse extends _ITankSetupFormBase {
  deviceId: number;
  tankNumber: number;
  minNumber: number;
  rtuNumber: number;
  rtuModelNumber: string;
  rtuModelType: string;
  wlanStatus: string;
  txSequence: number;
  rxSequence: number;
  batteryTypeDesc: string;
  totalBatteryTypeId: number;
  batteryOffset: number;
  maxCalls: number;
  overrideRtuSensor: boolean;
  rtuSensorTypeId: number;
  rtuSensorType: string;
  enableGPS: boolean;
  sensorInputType: string;
  sensorDescription: string;
  sensorRange: string;
  sensorRangeUom: string;
  sensorLength: string;
  sensorLengthUom: string;
  sensorNumber: string;
  vaporSensorRange: string;
  vaporSensorRangeUom: string;
  moPhoneNumber: string;
  wlanFrequency: string;
  batteryTypeId: number;
  enableWLAN: boolean;
  isFrequencyValueEnabled: boolean;
  isDeviceTankNumberEnabled: boolean;
  isSensorRangeEnabled: boolean;
  isVaporSensorRangeEnabled: boolean;
  isBatteryOffsetEnabled: boolean;
  isMaxCallCountEnabled: boolean;
  isRTUSensorTypeIdEnabled: boolean;
  isOverrideRTUSensorTypeEnabled: boolean;
  isEnableGPSEnabled: boolean;
  isMOPhoneNumberEnabled: boolean;
  isSensorDescriptionEnabled: boolean;
  isEnableWlanEnabled: boolean;
  isSensorLengthEnabled: boolean;
  totalBatteryTypes: DropDown<number>[];
  rtuSensorTypes: DropDown<number>[];
  tankNumberMaxValue: number;
  tankNumberMinValue: number;
  sensorRangeMaxValue: number;
  sensorRangeMinValue: number;
  vaporSensorRangeMaxValue: number;
  vaporSensorRangeMinValue: number;
  frequencyMinValue: number;
  frequencyMaxValue: number;
  tankNumberRangeTranslatedExMsg: string;
}

export interface ITankRtuDeviceSaveRequest {
  deviceId: number;
  enableWLAN: boolean;
  tankNumber?: number;
  totalBatteries?: number;
  batteryCallsOffset?: number;
  maxCalls?: number;
  overrideRtuSensor?: boolean;
  rtuSensorTypeId?: number;
  enableGPS?: boolean;
  sensorDescription: string;
  sensorRange: string;
  sensorLength: string;
  sensorNumber: string;
  vaporSensorRange: string;
  moPhoneNumber: string;
  wlanFrequency: string;
}
